// Shared heart battery constants and refill helpers (Story + Endless).
// Natural regen fills up to MAX_HEARTS. Purchases / ads / rewards may
// store more, up to HEART_STORAGE_CAP.

export const MAX_HEARTS = 3;
export const HEART_STORAGE_CAP = 999;
export const HEART_REFILL_MS = 60 * 60 * 1000;

/**
 * Ms until the next passive heart refill. 0 when at/above the regen
 * battery or ready now. Excess hearts above MAX_HEARTS also count as full
 * for regen (no passive refill while over the natural limit).
 */
export function getHeartRefillMsRemaining(
    hearts = MAX_HEARTS,
    heartLastRefillAt = Date.now(),
    now = Date.now(),
    maxHearts = MAX_HEARTS,
    refillMs = HEART_REFILL_MS
) {
    const safeHearts = Math.max(0, Math.floor(Number(hearts) || 0));
    if (safeHearts >= maxHearts) {
        return 0;
    }

    const lastAt = Number.isFinite(Number(heartLastRefillAt))
        ? Number(heartLastRefillAt)
        : now;
    return Math.max(0, refillMs - (now - lastAt));
}

/**
 * Human countdown for the Wait path (e.g. "47m", "1h 2m", "35s").
 */
export function formatHeartRefillCountdown(msRemaining = 0) {
    const ms = Math.max(0, Math.floor(Number(msRemaining) || 0));

    if (ms <= 0) {
        return "Ready";
    }

    const totalSeconds = Math.ceil(ms / 1000);

    if (ms < 60 * 1000) {
        return `${totalSeconds}s`;
    }

    const totalMinutes = Math.ceil(ms / 60000);

    if (totalMinutes < 60) {
        return `${totalMinutes}m`;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (minutes <= 0) {
        return `${hours}h`;
    }

    return `${hours}h ${minutes}m`;
}

/** Clamp to the storeable range (allows excess above the regen battery). */
export function clampStoredHearts(
    hearts,
    storageCap = HEART_STORAGE_CAP
) {
    return Math.max(
        0,
        Math.min(storageCap, Math.floor(Number(hearts) || 0))
    );
}

/** @deprecated Prefer clampStoredHearts — kept for callers that mean battery clamp. */
export function clampHearts(hearts, maxHearts = MAX_HEARTS) {
    return Math.max(0, Math.min(maxHearts, Math.floor(Number(hearts) || 0)));
}

/**
 * Add hearts from purchases, ads, or rewards. Can exceed MAX_HEARTS up to
 * the storage cap.
 */
export function grantHearts(
    currentHearts,
    amount = 1,
    storageCap = HEART_STORAGE_CAP
) {
    const add = Math.max(0, Math.floor(Number(amount) || 0));
    return clampStoredHearts((Number(currentHearts) || 0) + add, storageCap);
}

/**
 * Ensure the regen battery is full without reducing any excess stash.
 */
export function refillHeartsToMax(
    currentHearts = 0,
    batteryMax = MAX_HEARTS,
    storageCap = HEART_STORAGE_CAP
) {
    return clampStoredHearts(
        Math.max(Number(currentHearts) || 0, batteryMax),
        storageCap
    );
}

/**
 * Apply passive hourly regen. Only fills up to MAX_HEARTS; never reduces
 * a stash that is already at or above the natural regen limit.
 */
export function applyPassiveHeartRefill(
    hearts = MAX_HEARTS,
    heartLastRefillAt = Date.now(),
    now = Date.now(),
    batteryMax = MAX_HEARTS,
    refillMs = HEART_REFILL_MS,
    storageCap = HEART_STORAGE_CAP
) {
    const safeHearts = clampStoredHearts(hearts, storageCap);
    const safeLastRefillAt = Number.isFinite(Number(heartLastRefillAt))
        ? Number(heartLastRefillAt)
        : now;

    if (safeHearts >= batteryMax) {
        return {
            hearts: safeHearts,
            // Keep the stamp when over-battery so we don't churn timestamps;
            // refresh when exactly at battery so the next spend window is clean.
            heartLastRefillAt:
                safeHearts > batteryMax ? safeLastRefillAt : now,
        };
    }

    const elapsed = Math.max(0, now - safeLastRefillAt);
    const refilled = Math.floor(elapsed / refillMs);

    if (refilled <= 0) {
        return {
            hearts: safeHearts,
            heartLastRefillAt: safeLastRefillAt,
        };
    }

    const nextHearts = Math.min(batteryMax, safeHearts + refilled);

    return {
        hearts: nextHearts,
        heartLastRefillAt:
            nextHearts >= batteryMax
                ? now
                : safeLastRefillAt + refilled * refillMs,
    };
}

/**
 * Prefer the richer heart stash when merging guest local progress into an
 * account on login. Equal counts keep the earlier refill clock so regen is
 * not delayed.
 */
export function mergeHeartStates(localState = null, accountState = null) {
    if (!accountState && !localState) {
        return {
            hearts: MAX_HEARTS,
            heartLastRefillAt: Date.now(),
        };
    }
    if (!accountState) {
        return localState;
    }
    if (!localState) {
        return accountState;
    }

    const localHearts = Math.max(0, Math.floor(Number(localState.hearts) || 0));
    const accountHearts = Math.max(
        0,
        Math.floor(Number(accountState.hearts) || 0)
    );

    if (localHearts > accountHearts) {
        return localState;
    }
    if (accountHearts > localHearts) {
        return accountState;
    }

    const localAt = Number(localState.heartLastRefillAt) || 0;
    const accountAt = Number(accountState.heartLastRefillAt) || 0;
    return localAt <= accountAt ? localState : accountState;
}

/**
 * Display helper: always show a single heart icon plus the count.
 */
export function formatHeartsMeter(hearts) {
    const n = Math.max(0, Math.floor(Number(hearts) || 0));
    return `❤️ x${n}`;
}
